// ===========================================
// Teacher Time Off Card (teacher dashboard)
// ===========================================
// Lists the teacher's own time-off requests/periods with status chips,
// lets them request a new period (date range + reason) and withdraw a
// PENDING request. Approval is an admin action — PENDING never blocks
// availability; only APPROVED periods do.

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BeachAccess as TimeOffIcon,
  Close as WithdrawIcon,
} from '@mui/icons-material';
import { useMyUnavailability, useRequestTimeOff, useWithdrawTimeOff } from '../../hooks/useTeacherUnavailability';
import { todayPlusDaysString } from '../../utils/dateUtils';
import {
  TIME_OFF_STATUS_CHIP as STATUS_CHIP,
  timeOffRangeLabel as periodRangeLabel,
  timeOffErrorMessage as getErrorMessage,
} from '../../utils/timeOff';

// ===========================================
// REQUEST DIALOG
// ===========================================

interface RequestTimeOffDialogProps {
  open: boolean;
  onClose: () => void;
}

function RequestTimeOffDialog({ open, onClose }: RequestTimeOffDialogProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const requestMutation = useRequestTimeOff();

  const minDate = todayPlusDaysString(0);
  const rangeInvalid = !!startDate && !!endDate && endDate < startDate;
  const canSubmit = !!startDate && !!endDate && !rangeInvalid && !requestMutation.isPending;

  const handleClose = () => {
    requestMutation.reset();
    setStartDate('');
    setEndDate('');
    setReason('');
    onClose();
  };

  const handleSubmit = () => {
    requestMutation.mutate(
      { startDate, endDate, reason: reason.trim() || undefined },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Request Time Off</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Your request goes to the school admin for approval. Once approved, no new bookings can
            be placed for you on these days.
          </Typography>
          <TextField
            label="First day away"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDate }}
          />
          <TextField
            label="Last day away"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || minDate }}
            error={rangeInvalid}
            helperText={rangeInvalid ? 'Last day must be on or after the first day' : 'Both days are included'}
          />
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />
          {requestMutation.isError && (
            <Alert severity="error">{getErrorMessage(requestMutation.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {requestMutation.isPending ? 'Submitting…' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// CARD
// ===========================================

export default function TimeOffCard() {
  const [requestOpen, setRequestOpen] = useState(false);
  const { data: periods, isLoading, isError } = useMyUnavailability();
  const withdrawMutation = useWithdrawTimeOff();

  // Newest-first is the API order (startDate desc); show active/pending first,
  // and don't drown the card in old cancelled/declined rows.
  const visiblePeriods = useMemo(() => {
    const list = periods ?? [];
    const active = list.filter((p) => p.status === 'PENDING' || p.status === 'APPROVED');
    const inert = list.filter((p) => p.status === 'DECLINED' || p.status === 'CANCELLED').slice(0, 3);
    return [...active, ...inert];
  }, [periods]);

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeOffIcon color="primary" />
            <Typography variant="h6">Time Off</Typography>
          </Box>
        }
        action={
          <Button size="small" variant="outlined" onClick={() => setRequestOpen(true)}>
            Request Time Off
          </Button>
        }
        sx={{ pb: 0 }}
      />
      <Divider sx={{ mx: 2, mt: 2 }} />
      <CardContent>
        {isLoading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : isError ? (
          <Alert severity="error">Failed to load your time off. Please refresh.</Alert>
        ) : visiblePeriods.length > 0 ? (
          <List disablePadding>
            {visiblePeriods.map((period) => {
              const chip = STATUS_CHIP[period.status];
              return (
                <ListItem
                  key={period.id}
                  sx={{ px: 0, py: 1 }}
                  secondaryAction={
                    period.status === 'PENDING' ? (
                      <Tooltip title="Withdraw request">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Withdraw request"
                            disabled={withdrawMutation.isPending}
                            onClick={() => withdrawMutation.mutate(period.id)}
                          >
                            <WithdrawIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {periodRangeLabel(period)}
                        </Typography>
                        <Chip
                          label={chip.label}
                          size="small"
                          sx={{ bgcolor: chip.bgcolor, color: chip.color, fontWeight: 600 }}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        {period.reason && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {period.reason}
                          </Typography>
                        )}
                        {period.status === 'DECLINED' && period.reviewNote && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Admin note: {period.reviewNote}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <TimeOffIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              No time off requested
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Going away? Let the school know so nothing gets booked while you're gone.
            </Typography>
          </Box>
        )}
        {withdrawMutation.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {getErrorMessage(withdrawMutation.error)}
          </Alert>
        )}
      </CardContent>

      <RequestTimeOffDialog open={requestOpen} onClose={() => setRequestOpen(false)} />
    </Card>
  );
}
